import React, { useContext, useEffect, useRef, useState } from 'react';
import { InputRef } from 'antd';
import { Form, Input } from 'components';
import { EditableCellProps } from './types';
import { EditableContext } from './context';

const { TextArea } = Input;

export const EditableCell: React.FC<EditableCellProps> = ({
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  inputType = 'text',
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const contextValue = useContext(EditableContext);

  // Check if this cell should be in edit mode
  const shouldBeEditing =
    editable && record.key === contextValue.editingRow && dataIndex === contextValue.editingColumn;

  // When the cell should be in edit mode
  useEffect(() => {
    if (shouldBeEditing && !editing) {
      setEditing(true);
    }
  }, [shouldBeEditing, editing]);

  // When editing, focus and set value
  useEffect(() => {
    if (editing) {
      if (contextValue.form) {
        contextValue.form.setFieldsValue({ [dataIndex]: record[dataIndex] });
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [editing, dataIndex, record, contextValue.form]);

  const toggleEdit = () => {
    setEditing(!editing);
  };

  const save = async () => {
    try {
      if (contextValue.form) {
        const values = await contextValue.form.validateFields();
        toggleEdit();
        handleSave({ ...record, ...(values as any) });
      }
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: dataIndex === 'itemNumber',
            message: 'Required Field',
          },
        ]}
      >
        {inputType === 'number' ? (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} type="number" />
        ) : dataIndex === 'description' ? (
          <TextArea
            ref={inputRef as any}
            onBlur={save}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (!e.shiftKey) {
                  e.preventDefault();
                  save();
                }
                // Shift+Enter will create a new line by default
              }
            }}
          />
        ) : (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  return <td {...(restProps as any)}>{childNode}</td>;
};
